import React from 'react';

import { useStackNavigationContext, useTabNavigationContext } from 'core/navigation';
import { CASE1_ROUTE, CASE2_ROUTE, CASE3_ROUTE, ROOT_ROUTE } from '../navigation';
import { useAnalytics } from './Analytics.hook';

interface ContextValue
  extends Readonly<{
    onContactUsButtonPress: () => void;
  }> {}

const Context = React.createContext<ContextValue>({
  onContactUsButtonPress: () => {
    throw new Error('AnalyticsContextProvider not found!');
  },
});

export interface AnalyticsContextProviderProps
  extends Readonly<{
    children?: React.ReactNode;
  }> {}

export const AnalyticsContextProvider = React.memo<AnalyticsContextProviderProps>((props) => {
  const { children } = props;

  const { current } = useStackNavigationContext();
  const { selectedScreen } = useTabNavigationContext();
  const { onContactUsClick, onContentView } = useAnalytics();

  const OnContentViewCallback = React.useCallback(
    (stackScreen: string, tabScreen: string) => {
      if (stackScreen === ROOT_ROUTE.CASE1) {
        switch (tabScreen) {
          case CASE1_ROUTE.OVERVIEW:
            onContentView('Ensure superior network performance', 'Overview');
            break;
          case CASE1_ROUTE.CHALLENGES:
            onContentView('Ensure superior network performance', 'Challenges');
            break;
          case CASE1_ROUTE.BENEFITS:
            onContentView('Ensure superior network performance', 'Benefits');
            break;
          default:
            break;
        }

        return;
      }

      if (stackScreen === ROOT_ROUTE.CASE2) {
        switch (tabScreen) {
          case CASE2_ROUTE.OVERVIEW:
            onContentView('Create future-proof customer experience', 'Overview');
            break;
          case CASE2_ROUTE.CHALLENGES:
            onContentView('Create future-proof customer experience', 'Challenges');
            break;
          case CASE2_ROUTE.BENEFITS:
            onContentView('Create future-proof customer experience', 'Benefits');
            break;
          default:
            break;
        }

        return;
      }

      if (stackScreen === ROOT_ROUTE.CASE3) {
        switch (tabScreen) {
          case CASE3_ROUTE.OVERVIEW:
            onContentView('Turn operations into an efficient business enabler', 'Overview');
            break;
          case CASE3_ROUTE.CHALLENGES:
            onContentView('Turn operations into an efficient business enabler', 'Challenges');
            break;
          case CASE3_ROUTE.BENEFITS:
            onContentView('Turn operations into an efficient business enabler', 'Benefits');
            break;
          default:
            break;
        }
      }
    },
    [onContentView]
  );

  const OnContactUsButtonPressCallback = React.useCallback(() => {
    const { screen: stackScreen } = current;
    const { screen: tabScreen } = selectedScreen;

    if (stackScreen === ROOT_ROUTE.CASE1) {
      switch (tabScreen) {
        case CASE1_ROUTE.OVERVIEW:
          onContactUsClick('Ensure superior network performance', 'Overview');
          break;
        case CASE1_ROUTE.CHALLENGES:
          onContactUsClick('Ensure superior network performance', 'Challenges');
          break;
        case CASE1_ROUTE.BENEFITS:
          onContactUsClick('Ensure superior network performance', 'Benefits');
          break;
        default:
          break;
      }

      return;
    }

    if (stackScreen === ROOT_ROUTE.CASE2) {
      switch (tabScreen) {
        case CASE2_ROUTE.OVERVIEW:
          onContactUsClick('Create future-proof customer experience', 'Overview');
          break;
        case CASE2_ROUTE.CHALLENGES:
          onContactUsClick('Create future-proof customer experience', 'Challenges');
          break;
        case CASE2_ROUTE.BENEFITS:
          onContactUsClick('Create future-proof customer experience', 'Benefits');
          break;
        default:
          break;
      }

      return;
    }

    if (stackScreen === ROOT_ROUTE.CASE3) {
      switch (tabScreen) {
        case CASE3_ROUTE.OVERVIEW:
          onContactUsClick('Turn operations into an efficient business enabler', 'Overview');
          break;
        case CASE3_ROUTE.CHALLENGES:
          onContactUsClick('Turn operations into an efficient business enabler', 'Challenges');
          break;
        case CASE3_ROUTE.BENEFITS:
          onContactUsClick('Turn operations into an efficient business enabler', 'Benefits');
          break;
        default:
          break;
      }
    }
  }, [current, selectedScreen, onContactUsClick]);

  React.useEffect(() => {
    const { screen: stackScreen } = current;
    const { screen: tabScreen } = selectedScreen;

    OnContentViewCallback(stackScreen, tabScreen);
  }, [current, selectedScreen, OnContentViewCallback]);

  const ContextValue = React.useMemo<ContextValue>(() => {
    const contextValue: ContextValue = {
      onContactUsButtonPress: OnContactUsButtonPressCallback,
    };

    return contextValue;
  }, [OnContactUsButtonPressCallback]);

  return <Context.Provider value={ContextValue}>{children}</Context.Provider>;
});

export const useAnalyticsContext = () => React.useContext<ContextValue>(Context);
